import type { Market } from '../shared/domain/Market'
import { z } from 'zod'
import { Model } from './architecture'

export const DistanceToCityCenterValidation = z.object({
  value: z
    .number({ required_error: '[DistanceToCityCenter] value is required' })
    .min(0, '[DistanceToCityCenter] value must be 0 or greater'),
})

/**
 * This Model always returns the distance to the city center in kms
 * */
export class DistanceToCityCenter extends Model {
  static #CENTER_RADIUS = 0.6
  static ONE_KILOMETER_IN_MILES = 0.6213711922

  static create({ value }: z.infer<typeof DistanceToCityCenterValidation>) {
    DistanceToCityCenterValidation.parse({ value })
    return new DistanceToCityCenter(value)
  }

  static createWithUnit({ value, unit }: { value: number; unit: string }) {
    const { unit: distanceUnit = 'm', value: distanceValue } = { value, unit }
    const distanceInKms =
      distanceUnit === 'm' && distanceValue ? distanceValue / 1000 : distanceValue

    return DistanceToCityCenter.create({ value: Math.floor(distanceInKms) })
  }

  static fromKM({ value }: { value: number }) {
    const distanceInKms = Math.round(value * 10) / 10
    return DistanceToCityCenter.create({ value: distanceInKms })
  }

  static fromKmOrNull({ value }: { value?: number | null }) {
    try {
      if (!value) return null
      return this.fromKM({ value })
    } catch {
      return null
    }
  }

  constructor(private readonly _value: number) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  isInCenter() {
    return this.value <= DistanceToCityCenter.#CENTER_RADIUS
  }

  byMarket({ market }: { market: Market }) {
    if (market.isImperialMetric()) {
      const miles = this.value * DistanceToCityCenter.ONE_KILOMETER_IN_MILES
      return Math.round(miles * 10) / 10
    }
    return this.value
  }

  toJSON() {
    return { value: this.value }
  }
}
